import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Link from e-mail 02 (/verificar?token=…) — or e-mail + 6-character code typed by hand. */
const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const token = searchParams.get('token');
  const [state, setState] = useState(token ? 'verifying' : 'form'); // verifying | done | error | form
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const verify = async (body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/verify-email`, body);
      setState('done');
    } catch (e) {
      setState(token && !body.code ? 'error' : 'form-error');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (token) verify({ token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FAF8F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">{t('emailPages.verifyTitle')}</CardTitle>
          <CardDescription>{t('emailPages.verifyDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'verifying' && <p className="text-sm text-gray-600">{t('emailPages.verifying')}</p>}
          {state === 'done' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-testid="verify-done">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="font-semibold text-green-900">{t('emailPages.verified')}</p>
              <Link to="/shop" className="block mt-4">
                <Button className="w-full bg-[#be1f1a] hover:bg-[#a71b17]">{t('emailPages.startShopping')}</Button>
              </Link>
            </div>
          )}
          {state === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-800">{t('emailPages.linkExpired')}</p>
              <Button variant="outline" className="mt-3" onClick={() => setState('form')}>{t('emailPages.useCode')}</Button>
            </div>
          )}
          {(state === 'form' || state === 'form-error') && (
            <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); verify({ email, code }); }}>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input type="email" required className="pl-10" placeholder={t('emailPages.emailPlaceholder')}
                       value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <Input required maxLength={6} className="tracking-[0.4em] text-center uppercase"
                     placeholder={t('emailPages.codePlaceholder')} value={code}
                     onChange={(e) => setCode(e.target.value.toUpperCase())} />
              {state === 'form-error' && <p className="text-sm text-red-700">{t('emailPages.codeInvalid')}</p>}
              <Button type="submit" disabled={busy} className="w-full bg-[#be1f1a] hover:bg-[#a71b17]">
                {t('emailPages.verifyButton')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
